/** Admin routes: users & access, demo area, audit and access logs, system. Spread into `app/routeTree.ts`. */
import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { labelFrom } from '@/app/breadcrumbs';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { NoAccessError, requirePermissions } from '@/app/route-guards';
import type { Permission } from '@/auth/permissions';
import { filterParam } from '@/lib/url-state';
import { demoListQuery, demoSearchSchema, demoUserQuery } from './demo-area/api';
import {
  accessEntryQuery,
  accessListQuery,
  accessSearchSchema,
  auditEntryQuery,
  auditListQuery,
  auditSearchSchema,
} from './activity/api';
import { AccessEntryNotFound, AuditEntryNotFound, DemoUserNotFound, UserNotFound } from './not-found';
import { systemQuery } from './system/api';
import { userQuery, usersListQuery, usersSearchSchema } from './users/api';

/** A numeric id param, or the route's not-found state. */
function idParam(raw: string): number {
  const id = Number(raw);
  if (!Number.isInteger(id) || id <= 0) throw notFound();
  return id;
}

async function loadOr404<T>(run: () => Promise<T>) {
  try {
    return await run();
  } catch (error) {
    if (isApiError(error) && error.code === 'not_found') throw notFound();
    throw error;
  }
}

/** The demo area also sits behind the server's CrossOperatorGuard: root (SUPER_ADMIN) only. */
function requireSuperAdmin(context: RouterContext, ...permissions: Permission[]) {
  requirePermissions(context, ...permissions);
  if (!context.auth.superAdmin) throw new NoAccessError(permissions);
}

// ---- users & access ----------------------------------------------------------------------------

export const usersListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'users',
  validateSearch: usersSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_USERS'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(usersListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./users/pages/users-list-page'), 'UsersListPage'),
});

export const userCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'users/new',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_USERS', 'UPDATE_USERS'),
  staticData: { crumbs: () => [{ label: 'New user' }] },
  component: lazyRouteComponent(() => import('./users/pages/user-edit-page'), 'UserCreatePage'),
});

export const userDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'users/$id',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_USERS'),
  loader: ({ context, params }) => loadOr404(() => context.queryClient.ensureQueryData(userQuery(idParam(params.id)))),
  staticData: {
    crumbs: (params, user) => [{ label: labelFrom(user, 'FullName', 'Email') ?? `User ${params.id ?? ''}` }],
  },
  notFoundComponent: UserNotFound,
  component: lazyRouteComponent(() => import('./users/pages/user-detail-page'), 'UserDetailPage'),
});

export const userEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'users/$id/edit',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_USERS', 'UPDATE_USERS'),
  loader: ({ context, params }) => loadOr404(() => context.queryClient.ensureQueryData(userQuery(idParam(params.id)))),
  staticData: {
    crumbs: (params, user) => [
      { label: labelFrom(user, 'FullName', 'Email') ?? `User ${params.id ?? ''}`, href: `/users/${params.id ?? ''}` },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: UserNotFound,
  component: lazyRouteComponent(() => import('./users/pages/user-edit-page'), 'UserEditPage'),
});

// ---- demo area ---------------------------------------------------------------------------------

export const demoListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'demo-area',
  validateSearch: demoSearchSchema,
  beforeLoad: ({ context }) => requireSuperAdmin(context, 'VIEW_LIST_DEMOAREA'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(demoListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./demo-area/pages/demo-users-list-page'), 'DemoUsersListPage'),
});

export const demoCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'demo-area/new',
  beforeLoad: ({ context }) => requireSuperAdmin(context, 'UPDATE_DEMOAREA'),
  staticData: { crumbs: () => [{ label: 'New demo user' }] },
  component: lazyRouteComponent(() => import('./demo-area/pages/demo-user-edit-page'), 'DemoUserCreatePage'),
});

export const demoDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'demo-area/$id',
  beforeLoad: ({ context }) => requireSuperAdmin(context, 'VIEW_SINGLE_DEMOAREA'),
  loader: ({ context, params }) =>
    loadOr404(() => context.queryClient.ensureQueryData(demoUserQuery(idParam(params.id)))),
  staticData: { crumbs: (params) => [{ label: `Demo user ${params.id ?? ''}` }] },
  notFoundComponent: DemoUserNotFound,
  component: lazyRouteComponent(() => import('./demo-area/pages/demo-user-detail-page'), 'DemoUserDetailPage'),
});

export const demoEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'demo-area/$id/edit',
  beforeLoad: ({ context }) => requireSuperAdmin(context, 'VIEW_SINGLE_DEMOAREA', 'UPDATE_DEMOAREA'),
  loader: ({ context, params }) =>
    loadOr404(() => context.queryClient.ensureQueryData(demoUserQuery(idParam(params.id)))),
  staticData: {
    crumbs: (params) => [
      { label: `Demo user ${params.id ?? ''}`, href: `/demo-area/${params.id ?? ''}` },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: DemoUserNotFound,
  component: lazyRouteComponent(() => import('./demo-area/pages/demo-user-edit-page'), 'DemoUserEditPage'),
});

// ---- audit & access logs -----------------------------------------------------------------------

export const auditListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'audit',
  validateSearch: auditSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_USERS'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(auditListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./activity/pages/audit-log-pages'), 'AuditLogPage'),
});

export const auditDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'audit/$id',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_USERS'),
  loader: ({ context, params }) =>
    loadOr404(() => context.queryClient.ensureQueryData(auditEntryQuery(idParam(params.id)))),
  staticData: { crumbs: (params) => [{ label: `Entry ${params.id ?? ''}` }] },
  notFoundComponent: AuditEntryNotFound,
  component: lazyRouteComponent(() => import('./activity/pages/audit-log-pages'), 'AuditEntryPage'),
});

export const accessListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'access-log',
  validateSearch: accessSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_PLAYERS'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(accessListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./activity/pages/access-log-pages'), 'AccessLogPage'),
});

export const accessDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'access-log/$id',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PLAYERS'),
  loader: ({ context, params }) =>
    loadOr404(() => context.queryClient.ensureQueryData(accessEntryQuery(idParam(params.id)))),
  staticData: { crumbs: (params) => [{ label: `Entry ${params.id ?? ''}` }] },
  notFoundComponent: AccessEntryNotFound,
  component: lazyRouteComponent(() => import('./activity/pages/access-log-pages'), 'AccessEntryPage'),
});

// ---- system ------------------------------------------------------------------------------------

export const systemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'system',
  validateSearch: z.object({ confirm: filterParam.enum(['clear-cache']) }),
  // GET /system/1 needs the single-operator bit (the list endpoint is broken, api-traps #4).
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_OPERATORS'),
  loader: ({ context }) => context.queryClient.ensureQueryData(systemQuery()),
  component: lazyRouteComponent(() => import('./system/system-page'), 'SystemPage'),
});

export const adminRoutes = [
  usersListRoute,
  userCreateRoute,
  userDetailRoute,
  userEditRoute,
  demoListRoute,
  demoCreateRoute,
  demoDetailRoute,
  demoEditRoute,
  auditListRoute,
  auditDetailRoute,
  accessListRoute,
  accessDetailRoute,
  systemRoute,
] as const;
